import { CartAddEvent, ThemeEvents } from '@theme/events';

const CACHE_TTL = 5 * 60 * 1000;
const CACHE_LIMIT = 8;

/** One shared mobile dialog. Only explicit card/color clicks request product content. */
class SanolaMobileQuickAdd extends HTMLElement {
  #cache = new Map();
  #controller;
  #listeners;
  #request = 0;
  #variants = [];
  #selection = [];
  #variant;
  #loading = false;
  #submitting = false;
  #url;
  #trigger;
  #scroll;
  #bodyStyles;
  #heightLocked = false;
  #resizeFrame;
  #pointerInput = false;
  #media = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    document.addEventListener('pointerdown', () => this.#setPointerFocus(true), { signal, capture: true });
    document.addEventListener('keydown', () => this.#setPointerFocus(false), { signal, capture: true });
    document.addEventListener('click', this.#openFromCard, { signal, capture: true });
    document.addEventListener(ThemeEvents.cartUpdate, () => this.#cache.clear(), { signal });
    this.addEventListener('click', this.#click, { signal });
    this.addEventListener('submit', this.#submit, { signal });
    this.addEventListener('scroll', (event) => {
      if (event.target.matches?.('[data-quick-add-images]')) this.#galleryPosition();
    }, { signal, capture: true });
    this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientY < rect.top || event.clientY > rect.bottom || event.clientX < rect.left || event.clientX > rect.right) this.close();
    }, { signal });
    this.#media.addEventListener('change', () => { if (!this.#media.matches) this.close(); }, { signal });
    window.addEventListener('resize', this.#resize, { signal });
    window.visualViewport?.addEventListener('resize', this.#resize, { signal });
    // The lightweight button handler remembers taps before this module has loaded.
    const pendingTrigger = window.sanolaQuickAddPending;
    this.#setPointerFocus(Boolean(window.sanolaQuickAddPendingPointer));
    delete window.sanolaQuickAddPendingPointer;
    delete window.sanolaQuickAddPending;
    if (pendingTrigger?.isConnected && this.#media.matches) pendingTrigger.click();
  }

  disconnectedCallback() {
    this.close();
    this.#listeners?.abort();
  }

  get dialog() { return this.querySelector('dialog'); }
  get product() { return this.querySelector('[data-quick-add-product]'); }
  get mount() { return this.querySelector('[data-quick-add-mount]'); }

  #openFromCard = (event) => {
    const trigger = event.target.closest?.('[data-sanola-mobile-quick-add]');
    if (!trigger || !this.#media.matches || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.#submitting) return;
    this.#trigger?.removeAttribute('data-pointer-focus');
    this.#trigger = trigger;
    this.#setPointerFocus(event.isTrusted ? event.detail > 0 : this.#pointerInput);
    const cardLink = trigger.closest('product-card')?.querySelector('[ref="productCardLink"]');
    const url = cardLink?.href || trigger.dataset.productUrl;
    this.#showGuide(false);
    this.mount.replaceChildren(this.querySelector('[data-quick-add-placeholder-template]').content.cloneNode(true));
    this.#heightLocked = false;
    this.dialog.style.removeProperty('height');
    this.#variants = [];
    this.#selection = [];
    this.#variant = null;
    this.#status('');
    this.querySelector('[data-quick-add-announcement]').textContent = '';
    if (!this.dialog.open) {
      this.#scroll = window.scrollY;
      this.#bodyStyles = ['position', 'top', 'width'].map((key) => document.body.style[key]);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#scroll}px`;
      document.body.style.width = '100%';
      this.dialog.showModal();
    }
    this.#load(url);
  };

  close({ preserveCart = false } = {}) {
    if (this.dialog?.hasAttribute('data-cart-handoff') && !preserveCart) return;
    this.#controller?.abort();
    cancelAnimationFrame(this.#resizeFrame);
    ++this.#request;
    if (!this.dialog?.open) return;
    this.dialog.close();
    if (!preserveCart) {
      ['position', 'top', 'width'].forEach((key, i) => { document.body.style[key] = this.#bodyStyles?.[i] || ''; });
      window.scrollTo({ top: this.#scroll || 0, behavior: 'instant' });
    }
    this.#showGuide(false);
    if (!preserveCart) this.#trigger?.focus({ preventScroll: true });
  }

  #setPointerFocus(pointer) {
    this.#pointerInput = pointer;
    this.querySelector('[data-quick-add-close]')?.toggleAttribute('data-pointer-focus', pointer);
    this.#trigger?.toggleAttribute('data-pointer-focus', pointer);
  }

  #resize = () => {
    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = requestAnimationFrame(() => {
      if (this.dialog.open && this.product) {
        this.#fitHeight();
        this.#galleryPosition();
      }
    });
  };

  #fitHeight() {
    const main = this.product.querySelector('[data-quick-add-main]');
    const guide = this.product.querySelector('[data-guide-content]');
    const mainScroll = main.scrollTop;
    const guideScroll = guide?.scrollTop;
    this.dialog.setAttribute('data-measuring', '');
    const naturalHeight = Math.ceil(this.dialog.getBoundingClientRect().height);
    this.dialog.removeAttribute('data-measuring');
    const viewportHeight = Math.min(window.innerHeight, window.visualViewport?.height || window.innerHeight);
    this.dialog.style.height = `${Math.min(naturalHeight, viewportHeight * 0.95)}px`;
    main.scrollTop = mainScroll;
    if (guide) guide.scrollTop = guideScroll;
    this.#heightLocked = true;
  }

  #status(text) {
    this.querySelector('[data-quick-add-status]').textContent = text;
  }

  #busy(busy) {
    this.#loading = busy;
    this.mount.setAttribute('aria-busy', String(busy));
    this.#updateSelection();
  }

  async #load(href, preserveSize = false) {
    const request = ++this.#request;
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    const url = new URL(href, location.origin);
    if (url.origin !== location.origin) return;
    // Product URLs already contain the storefront locale/market context.
    url.searchParams.delete('section_id');
    url.searchParams.delete('variant');
    this.#url = url.href;
    const previousSizes = new Map();
    if (preserveSize) {
      this.product?.querySelectorAll('[data-option-position]').forEach((field) => {
        if (/size/i.test(field.dataset.optionName)) previousSizes.set(field.dataset.optionName.toLowerCase(), this.#selection[Number(field.dataset.optionPosition)]);
      });
    }
    this.#showGuide(false);
    if (!this.product && !this.mount.firstElementChild) {
      this.mount.replaceChildren(this.querySelector('[data-quick-add-placeholder-template]').content.cloneNode(true));
    }
    this.#busy(true);
    this.#status('');
    this.querySelector('[data-quick-add-recovery]').hidden = true;
    this.querySelector('[data-quick-add-fallback]').href = this.#url;
    try {
      let cached = this.#cache.get(this.#url);
      if (!cached || Date.now() - cached.at > CACHE_TTL) {
        url.searchParams.set('section_id', 'quick-add-mobile-product-sanola');
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Product request failed');
        const html = await response.text();
        if (request !== this.#request || !this.dialog.open) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const product = doc.querySelector('[data-quick-add-product]');
        if (!product) throw new Error('Missing product content');
        cached = { html: product.outerHTML, at: Date.now() };
        this.#cache.delete(this.#url);
        this.#cache.set(this.#url, cached);
        if (this.#cache.size > CACHE_LIMIT) this.#cache.delete(this.#cache.keys().next().value);
      }
      if (request !== this.#request || !this.dialog.open) return;
      const template = document.createElement('template');
      template.innerHTML = cached.html;
      const product = template.content.querySelector('[data-quick-add-product]');
      const variants = JSON.parse(product.querySelector('[data-quick-add-variants]')?.textContent || '[]');
      // Validate before replacing the current view, so failures keep a usable recovery link.
      if (!Array.isArray(variants)) throw new Error('Invalid variant data');
      this.mount.replaceChildren(template.content);
      this.#variants = variants;
      const fields = [...this.product.querySelectorAll('[data-option-position]')];
      this.#selection = fields.map((field) => {
        const size = previousSizes.get(field.dataset.optionName.toLowerCase());
        const position = Number(field.dataset.optionPosition);
        if (size && variants.some((variant) => variant.available && variant.options[position] === size)) return size;
        const values = field.querySelectorAll('[data-option-value]');
        return values.length === 1 ? values[0].dataset.optionValue : null;
      });
      const guideContent = this.product.querySelector('[data-guide-content] .size-guide-drawer__content');
      if (guideContent) {
        const hasInches = [...guideContent.querySelectorAll('.size-guide-drawer__table-wrap--in td')].some((cell) => cell.textContent.trim() && cell.textContent.trim() !== '—');
        const unit = hasInches ? 'in' : 'cm';
        this.#setGuideUnit(guideContent, unit);
      }
      this.#busy(false);
      this.#galleryPosition();
      this.#status('');
      if (!this.#heightLocked) this.#fitHeight();
      if (preserveSize) this.product.querySelector('[data-sibling-swatches] [aria-current="page"]')?.focus({ preventScroll: true });
    } catch (error) {
      if (request !== this.#request || error.name === 'AbortError') return;
      if (!this.product) this.mount.replaceChildren();
      this.#status(this.dataset.loadError);
      this.querySelector('[data-quick-add-recovery]').hidden = false;
      // Keep purchase disabled until the requested color has loaded successfully.
      this.#busy(true);
      this.mount.setAttribute('aria-busy', 'false');
    }
  }

  #updateSelection() {
    if (!this.product) return;
    const fields = [...this.product.querySelectorAll('[data-option-position]')];
    this.#variant = this.#variants.find((variant) => !fields.length || variant.options.every((value, i) => value === this.#selection[i]));
    fields.forEach((field, index) => {
      if (this.#selection[index]) {
        field.removeAttribute('aria-invalid');
        field.removeAttribute('aria-describedby');
        const error = field.querySelector('[data-option-error]');
        if (error) error.hidden = true;
      }
      field.querySelector('[data-selected-option]').textContent = this.#selection[index] || '';
      field.querySelectorAll('[data-option-value]').forEach((button) => {
        const value = button.dataset.optionValue;
        const available = this.#variants.some((variant) => variant.available && variant.options[index] === value && variant.options.every((other, i) => i >= index || !this.#selection[i] || other === this.#selection[i]));
        button.setAttribute('aria-pressed', String(this.#selection[index] === value));
        button.disabled = this.#loading || this.#submitting || !available;
        button.classList.toggle('is-unavailable', !available);
      });
    });
    const button = this.product.querySelector('[data-quick-add-submit]');
    const input = this.product.querySelector('input[name="id"]');
    if (!button || !input) return;
    const ready = this.#variant?.available && !this.#loading && !this.#submitting;
    const anyAvailable = this.#variants.some((variant) => variant.available);
    button.disabled = this.#loading || this.#submitting || !anyAvailable || Boolean(this.#variant && !this.#variant.available);
    input.disabled = !ready;
    input.value = this.#variant?.id || '';
    button.textContent = this.#submitting ? this.dataset.adding : this.#variant ? (this.#variant.available ? this.dataset.add : this.dataset.soldOut) : this.dataset.add;
    if (!this.#submitting && !this.#variants.some((variant) => variant.available)) button.textContent = this.dataset.soldOut;
    const url = new URL(this.product.dataset.productUrl, location.origin);
    if (this.#variant) {
      url.searchParams.set('variant', this.#variant.id);
      const price = this.product.querySelector(`[data-variant-price="${this.#variant.id}"]`);
      if (price) this.product.querySelector('[data-quick-add-price]').replaceChildren(price.content.cloneNode(true));
    }
    this.product.querySelector('[data-quick-add-details]').href = url.href;
  }

  #showGuide(show) {
    const guide = this.product?.querySelector('[data-quick-add-guide]');
    this.querySelector('#sanola-mobile-quick-add-heading').textContent = show && guide ? this.dataset.guideHeading : this.dataset.productHeading;
    if (!guide) return;
    guide.hidden = !show;
    this.product.querySelector('[data-quick-add-main]').hidden = show;
    this.product.querySelector('[data-quick-add-purchase]').hidden = show;
    if (show) this.product.querySelector('[data-guide-back]').focus({ preventScroll: true });
  }

  #setGuideUnit(content, unit) {
    content.dataset.sizeGuideUnit = unit;
    content.querySelectorAll('[data-size-guide-unit-button]').forEach((button) => {
      const active = button.dataset.sizeGuideUnitButton === unit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  #galleryPosition = () => {
    const rail = this.product?.querySelector('[data-quick-add-images]');
    if (!rail) return;
    const position = Math.abs(rail.scrollLeft);
    const maximum = rail.scrollWidth - rail.clientWidth;
    this.product.querySelectorAll('[data-gallery-step]').forEach((button) => {
      const hidden = maximum <= 1 || (Number(button.dataset.galleryStep) < 0 ? position <= 1 : position >= maximum - 1);
      button.hidden = hidden;
    });
  };

  #click = (event) => {
    const target = event.target.closest('button, a');
    if (!target) return;
    if (target.matches('[data-quick-add-close]')) return this.close();
    if (target.matches('[data-guide-back]')) {
      this.#showGuide(false);
      this.product.querySelector('[data-guide-open]')?.focus({ preventScroll: true });
      return;
    }
    if (target.matches('[data-quick-add-retry]')) return this.#load(this.#url, true);
    if (target.matches('[data-sibling-swatches] a')) {
      event.preventDefault();
      if (!this.#submitting) this.#load(target.href, true);
      return;
    }
    if (this.#loading || this.#submitting) return;
    if (target.matches('[data-option-value]')) {
      const previousMedia = this.#variant?.mediaId;
      const position = Number(target.closest('[data-option-position]').dataset.optionPosition);
      this.#selection[position] = target.dataset.optionValue;
      for (let i = position + 1; i < this.#selection.length; i++) {
        if (!this.#variants.some((variant) => variant.available && variant.options.every((value, index) => index > i || !this.#selection[index] || value === this.#selection[index]))) this.#selection[i] = null;
      }
      this.#updateSelection();
      if (this.#variant?.mediaId && this.#variant.mediaId !== previousMedia) {
        const rail = this.product.querySelector('[data-quick-add-images]');
        const media = rail?.querySelector(`[data-media-id="${this.#variant.mediaId}"]`);
        if (media) rail.scrollBy({ left: media.getBoundingClientRect().left - rail.getBoundingClientRect().left - 8, behavior: 'instant' });
      }
    } else if (target.matches('[data-guide-open]')) {
      this.#showGuide(true);
    } else if (target.matches('[data-size-guide-unit-button]')) {
      this.#setGuideUnit(target.closest('.size-guide-drawer__content'), target.dataset.sizeGuideUnitButton);
    } else if (target.matches('[data-gallery-step]')) {
      const rail = this.product.querySelector('[data-quick-add-images]');
      const direction = getComputedStyle(rail).direction === 'rtl' ? -1 : 1;
      rail.scrollBy({ left: (rail.firstElementChild.getBoundingClientRect().width + 4) * Number(target.dataset.galleryStep) * direction, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
  };

  /** Keep the product sheet above the rendered cart until the handoff finishes. */
  #revealCart(drawer, cartEvent) {
    return new Promise((resolve) => {
      const controller = new AbortController();
      const { signal } = controller;
      const finish = () => { clearTimeout(timeout); controller.abort(); resolve(); };
      // The addition already succeeded: never offer a second submission on refresh failure.
      const recover = () => { finish(); window.location.assign(Theme.routes.cart_url); };
      const timeout = setTimeout(recover, 10000);
      drawer.addEventListener('sanola:cart-render-error', (event) => {
        if (event.detail.sourceEvent === cartEvent) recover();
      }, { signal });
      drawer.addEventListener('sanola:cart-rendered', (event) => {
        if (event.detail.sourceEvent !== cartEvent) return;
        // Own this opening so the normal auto-open listener cannot schedule a second one.
        event.stopImmediatePropagation();
        const quickAddOpen = this.dialog.open;
        const cartDialog = drawer.querySelector('dialog');
        if (cartDialog.open) { this.close({ preserveCart: true }); finish(); return; }
        cartDialog.setAttribute('data-quick-add-handoff', '');
        drawer.addEventListener('dialog:open', async () => {
          if (quickAddOpen && this.dialog.open) {
            // Native modal order follows showModal(), not z-index. Re-stack synchronously,
            // before paint, so quick add stays above the newly opened cart.
            this.dialog.setAttribute('data-cart-handoff', '');
            this.dialog.close();
            this.dialog.showModal();
            if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
              await this.dialog.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(100%)' }],
                { duration: 180, easing: 'ease-in', fill: 'forwards' }).finished.catch(() => {});
            }
            this.close({ preserveCart: true });
            this.dialog.getAnimations().forEach((animation) => animation.cancel());
            this.dialog.removeAttribute('data-cart-handoff');
          }
          cartDialog.querySelector('[autofocus]')?.focus({ preventScroll: true });
          const trigger = this.#trigger;
          drawer.addEventListener('dialog:close', () => {
            cartDialog.removeAttribute('data-quick-add-handoff');
            if (trigger?.isConnected) trigger.focus({ preventScroll: true });
          }, { once: true });
          finish();
        }, { once: true, signal });
        drawer.showDialog(quickAddOpen ? this.#scroll : window.scrollY);
      }, { capture: true, signal });
      this.dispatchEvent(cartEvent);
    });
  }

  #submit = async (event) => {
    event.preventDefault();
    if (this.#loading || this.#submitting) return;
    if (!this.#variant) {
      const missing = [...this.product.querySelectorAll('[data-option-position]')].filter((field) => !this.#selection[Number(field.dataset.optionPosition)]);
      missing.forEach((field) => {
        field.setAttribute('aria-invalid', 'true');
        const error = field.querySelector('[data-option-error]');
        if (error) {
          error.textContent = /size/i.test(field.dataset.optionName) ? this.dataset.selectSize : this.dataset.selectOptions;
          error.hidden = false;
          field.setAttribute('aria-describedby', error.id);
        }
      });
      missing[0]?.querySelector('[data-option-value]:not(:disabled)')?.focus();
      return;
    }
    if (!this.#variant.available) return;
    const variant = this.#variant;
    const productId = this.product.dataset.productId;
    const body = new FormData(event.target);
    this.#submitting = true;
    this.#updateSelection();
    this.#status('');
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId))].slice(0, 5);
    body.set('sections', sections.join(','));
    body.set('sections_url', location.pathname);
    try {
      // Same bundled-section/cart event contract used by Horizon's product form.
      const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', body, headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok || result.status) {
        this.#cache.clear();
        this.#status(typeof result.description === 'string' ? result.description : this.dataset.cartError);
        return;
      }
      this.#cache.clear();
      const cartEvent = new CartAddEvent({}, variant.id, { source: 'product-form-component', itemCount: 1, productId, variantId: variant.id, sections: result.sections });
      const drawer = document.querySelector('cart-drawer-component[auto-open]');
      if (drawer && this.dialog.open) {
        await this.#revealCart(drawer, cartEvent);
      } else {
        this.close();
        this.dispatchEvent(cartEvent);
      }
      this.querySelector('[data-quick-add-announcement]').textContent = this.dataset.added;
    } catch {
      this.#status(this.dataset.cartError);
    } finally {
      this.#submitting = false;
      this.#updateSelection();
    }
  };
}

if (!customElements.get('sanola-mobile-quick-add')) customElements.define('sanola-mobile-quick-add', SanolaMobileQuickAdd);
